import { forwardRef, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Box, BoxProps, useTheme } from "@mui/material";

import FeedbackDialog from "components/FeedbackDialog";
import { OnSendProps } from "components/FeedbackDialog/FeedbackDialog";
import useNotifier from "hooks/useNotifier";
import FeedbackApi from "api/feedback";
import useBoundStore from "store";
import { getInfo } from "utils/device";

export interface PageLayoutProps {
  appBar: React.ReactNode;
  children: React.ReactNode;
  containerSx?: BoxProps["sx"];
  fab?: React.ReactNode;
  /**
   * If true, renders a feedback button
   *
   * Default: false
   */
  feedback?: boolean;
  /**
   * Used to decrease the size of the inner container by the provided value.
   *
   * Default: '0px'
   */
  minusHeight?: string;
  /**
   * For pages which have a virtualized list, this is set to false and the
   * padding is controlled from within the virtualized list container.
   *
   * Default: true
   */
  paddingBottom?: boolean;
}

function PageLayout(props: PageLayoutProps, ref: React.Ref<HTMLDivElement>) {
  const { appBar, children, containerSx, fab, feedback = false, minusHeight = "0px", paddingBottom = true } = props;
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const userAuth = useBoundStore((state) => state.user.auth);
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const appStatus = useBoundStore((state) => state.miscellaneous.appStatus);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { showNotification } = useNotifier();
  const feedbackApi = useMemo(() => new FeedbackApi(showNotification), [showNotification]);

  const handleOpenFeedback = () => {
    setOpen(true);
    navigate(location.pathname);
  };

  const handleCloseFeedback = () => {
    setOpen(false);
    navigate(-1);
  };

  const handleSendFeedback = async (data: OnSendProps) => {
    const { error } = await feedbackApi.sendFeedback({
      details: data.details,
      route: location.pathname,
      uid: userAuth?.id.toString() ?? null,
      user_agent: await getInfo(),
      user_satisfaction: data.userSatisfaction,
    });
    if (!error) {
      showNotification({
        message: "Feedback successfully sent. Thank you!",
        severity: "success",
      });
    } else {
      showNotification({
        message: "Unable to send feedback.",
        severity: "error",
      });
    }
  };

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setOpen(false);
    }
  }, [navigationType]);

  return (
    <Box
      sx={
        isMobileDevice
          ? { height: "100vh", position: "relative" }
          : {
              bgcolor: "background.paper",
              borderRadius: 5,
              height: "calc(100vh - 2rem)",
              position: "relative",
            }
      }
    >
      {appBar}
      <Box
        ref={ref}
        sx={{
          pb: paddingBottom ? "36px" : undefined,
          position: "relative",
          overflow: "auto",
          height: `calc(100% - 3.5rem - ${minusHeight})`,
          [theme.breakpoints.down("sm")]: {
            "@media (orientation: landscape)": {
              height: `calc(100% - 3rem - ${minusHeight})`,
            },
          },
          [theme.breakpoints.up("sm")]: {
            height: `calc(100% - 4rem - ${minusHeight})`,
          },
          ...containerSx,
        }}
        style={{ paddingBottom: paddingBottom ? "36px" : undefined }}
      >
        {children}
      </Box>
      {appStatus?.showFeedback && feedback ? (
        <FeedbackDialog
          fabSx={{ position: isMobileDevice ? "fixed" : "absolute", right: "1.5rem", bottom: fab ? "6rem" : "1.5rem" }}
          onClose={handleCloseFeedback}
          onOpen={handleOpenFeedback}
          onSend={handleSendFeedback}
          open={open}
        />
      ) : null}
      {fab}
    </Box>
  );
}

const PageLayoutWithRef = forwardRef(PageLayout);

export default PageLayoutWithRef;
